'use client'
import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { ChevronDownIcon } from 'Icons'

export type FAQ = {
  question: string
  answer: string
}

type FAQItemProps = {
  faq: FAQ
  isOpen: boolean
  onToggle: () => void
}

// FAQ 单个项组件：默认问答按钮区域的高度保持一致（比如固定内边距），保证全部关闭时左右对齐
function FAQItem({ faq, isOpen, onToggle }: FAQItemProps) {
  return (
    <div className="mx-auto mb-4 rounded-md border border-white/15 bg-[rgba(116,92,68,0.15)]">
      <button
        className="flex h-full w-full items-center justify-between p-4 text-left hover:bg-white/10 focus:outline-none lg:min-h-[88px]"
        onClick={onToggle}
      >
        <span className="pr-2 text-base font-medium md:text-lg">
          {faq.question}
        </span>
        <motion.div
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.3, ease: 'easeInOut' }}
        >
          <ChevronDownIcon className="text-base" />
        </motion.div>
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
          >
            <div className="border-t border-white/15 p-4 text-[13px] text-gray-400 md:text-base">
              <p>{faq.answer}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default function FAQList({ faqs }: { faqs: FAQ[] }) {
  // 为了实现每个 FAQ 独立展开，这里用一个布尔数组保存各项的状态
  const [openStates, setOpenStates] = useState<boolean[]>(faqs.map(() => false))

  const toggleFAQ = (index: number) => {
    setOpenStates((states) => {
      const newStates = [...states]
      newStates[index] = !newStates[index]
      return newStates
    })
  }

  // 拆分 FAQ 列表为左右两列（索引偶数的放左边，奇数的放右边）
  const leftFaqs = faqs.filter((_, index) => index % 2 === 0)
  const rightFaqs = faqs.filter((_, index) => index % 2 === 1)

  return (
    <>
      {/* 移动端：单列显示 */}
      <div className="block lg:hidden">
        {faqs.map((faq, index) => (
          <FAQItem
            key={index}
            faq={faq}
            isOpen={openStates[index]}
            onToggle={() => toggleFAQ(index)}
          />
        ))}
      </div>

      {/* 桌面端：两列布局，每列内部独立 */}
      <div className="hidden lg:flex lg:space-x-4">
        <div className="flex-1">
          {leftFaqs.map((faq, idx) => {
            // 原数组中偶数项的索引即为 idx*2
            const index = idx * 2
            return (
              <FAQItem
                key={index}
                faq={faq}
                isOpen={openStates[index]}
                onToggle={() => toggleFAQ(index)}
              />
            )
          })}
        </div>
        <div className="flex-1">
          {rightFaqs.map((faq, idx) => {
            // 奇数项的索引即为 idx*2+1
            const index = idx * 2 + 1
            return (
              <FAQItem
                key={index}
                faq={faq}
                isOpen={openStates[index]}
                onToggle={() => toggleFAQ(index)}
              />
            )
          })}
        </div>
      </div>
    </>
  )
}
